
import { Component, Vue, Watch } from 'vue-property-decorator';
import RoleRepository, { Roles } from '@/services/Roles';
import HasScope from '@/components/Mixins/HasScope';
import { RoleInterface } from '@/services/Roles/Interfaces';
import { DataTableHeader } from 'vuetify';
import DeleteRoleDialog from '@/components/dialogs/roles/DeleteRoleDialog.vue';
import ScopesRepository from '@/services/Scopes';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';


@Component({
  mixins: [
    HasScope,
  ],
  components: {
    DeleteRoleDialog,
    PageHeader,
  },
  inject: [
    'rolesRepository',
    'scopesRepository',
  ],
})
export default class UserIndex extends Vue {
  private readonly rolesRepository!: RoleRepository;
  private readonly scopesRepository!: ScopesRepository;
  private loading: boolean = true;
  private createRoleScope: string = Roles.USER_ROLES_CREATE;
  private syncRoleScopes: string = Roles.USER_SCOPES_SYNC;
  private search: string = '';
  private roles: RoleInterface[] = [];
  private deleteRoleForDialog: RoleInterface | false = false;
  private deleteRoleDialog: string = '';
  private options: ListPaginationOptions = defaultListOptions();
  private readonly LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Role', value: 'name' },
    { text: 'Department', value: 'department.name' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  private created(): void {
    this.applyRouteQuery();
    this.initialize();
  }

  private applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      const parsed = parseListQuery(this.$route.query);
      this.options.page = parsed.page;
      this.options.itemsPerPage = parsed.itemsPerPage;
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  private async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({
        name: 'role_index',
        query: buildListPaginationQuery(this.options),
      });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  @Watch('options.page')
  @Watch('options.itemsPerPage')
  private async onPaginationChange(): Promise<void> {
    await this.syncRouteQuery();
  }

  @Watch('$route')
  private onRouteChange(): void {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'role_index') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }
    this.applyRouteQuery();
  }

  private async initialize(): Promise<void> {
    this.roles = await this.rolesRepository.getAllRoles();
    this.loading = false;
  }

  private editRole(role: RoleInterface): void {
    this.$router.push({ name: 'role_edit', params: { roleId: role.id } });
  }

  private deleteRole(role: RoleInterface): void {
    this.deleteRoleDialog = 'delete-role-dialog';
    this.deleteRoleForDialog = role;
  }

  private createRole(): void {
    this.$router.push({ name: 'role_create' });
  }

  private filterRoles(roleId: string): void {
    this.roles = this.roles.filter((role) => {
      return role.id !== roleId;
    });
  }

  private async syncScopes(): Promise<void> {
    this.loading = true;
    await this.scopesRepository.syncScopes();
    await this.initialize();
  }
}
